import {
  AnimationDirection,
  ESdChangeReason,
  IKuikaStyle,
  ILoopingAnimationItem,
  ILoopingKeyframeStop,
  InvisibleStylePart,
  TransitionTimingFunction,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSlider from "../../../ui-kit/kuika-slider/kuika-slider";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KeyframeStopEditor from "./keyframe-stop-editor";
import {
  ANIMATION_DIRECTION_OPTIONS,
  defaultLoopingAnimationItem,
  detectLoopingPreset,
  LOOPING_ANIMATION_DEFAULT_DURATION,
  LOOPING_PRESET_OPTIONS,
  LoopingPresetKey,
  summarizeLoopingAnimation
} from "./looping-animation-helpers";
import PopoverInput from "./popover-input/popover-input";
import { TRANSITION_EASING_OPTIONS } from "./styling-effects-helpers";
import { styleSectionNeedsRender } from "./styling-window-helpers";

const DURATION_MIN: number = 0;
const DURATION_MAX: number = 10000;
const DELAY_MIN: number = 0;
const DELAY_MAX: number = 10000;
const ITERATION_MIN: number = 1;
const ITERATION_MAX: number = 100;

const createAnimation = (): ILoopingAnimationItem => defaultLoopingAnimationItem("pulse");

interface AnimationPopoverContentProps {
  item: ILoopingAnimationItem;
  onPatch: (patch: Partial<ILoopingAnimationItem>) => void;
  onSelectPreset: (presetKey: LoopingPresetKey) => void;
  onKeyframesChange: (stops: ILoopingKeyframeStop[]) => void;
}

const AnimationPopoverContent: React.FC<AnimationPopoverContentProps> = ({
  item,
  onPatch,
  onSelectPreset,
  onKeyframesChange
}) => {
  const isInfinite: boolean = item.infinite !== false;

  return (
    <div className="effects-animation__popover">
      <div className="effects-animation__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Preset</KuikaSmallText>
        <KuikaSelectbox
          size="sm"
          options={LOOPING_PRESET_OPTIONS}
          value={detectLoopingPreset(item)}
          onChange={(next: string | number | (string | number)[] | null): void => {
            if (typeof next === "string") onSelectPreset(next as LoopingPresetKey);
          }}
          dataTestId="effects-animation-preset"
        />
      </div>
      <div className="effects-animation__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Duration</KuikaSmallText>
        <KuikaSlider
          value={item.durationMs ?? LOOPING_ANIMATION_DEFAULT_DURATION}
          onChange={(next: number): void => onPatch({ durationMs: next })}
          min={DURATION_MIN}
          max={DURATION_MAX}
          step={50}
          precision={0}
          unit="ms"
          size="sm"
          dataTestId="effects-animation-duration"
        />
      </div>
      <div className="effects-animation__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Easing</KuikaSmallText>
        <KuikaSelectbox
          size="sm"
          options={TRANSITION_EASING_OPTIONS}
          value={item.timingFunction ?? "linear"}
          onChange={(next: string | number | (string | number)[] | null): void => {
            if (typeof next === "string") onPatch({ timingFunction: next as TransitionTimingFunction });
          }}
          dataTestId="effects-animation-easing"
        />
      </div>
      <div className="effects-animation__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Delay</KuikaSmallText>
        <KuikaInput
          type="number"
          size="sm"
          value={item.delayMs ?? 0}
          rightUnit="ms"
          min={DELAY_MIN}
          max={DELAY_MAX}
          onChange={(_e, next: string | number): void => {
            const num: number = typeof next === "number" ? next : Number(next);
            if (!Number.isNaN(num)) onPatch({ delayMs: num });
          }}
          data-testid="effects-animation-delay"
        />
      </div>
      <div className="effects-animation__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Loop</KuikaSmallText>
        <KuikaSwitch
          label="Infinite"
          checked={isInfinite}
          onChange={(checked: boolean): void => onPatch({ infinite: checked })}
        />
      </div>
      {!isInfinite && (
        <div className="effects-animation__row">
          <KuikaSmallText style={{ color: "var(--dark-2)" }}>Repeat</KuikaSmallText>
          <KuikaInput
            type="number"
            size="sm"
            value={item.iterationCount ?? 1}
            rightUnit="×"
            min={ITERATION_MIN}
            max={ITERATION_MAX}
            onChange={(_e, next: string | number): void => {
              const num: number = typeof next === "number" ? next : Number(next);
              if (!Number.isNaN(num)) onPatch({ iterationCount: num });
            }}
            data-testid="effects-animation-iterations"
          />
        </div>
      )}
      <div className="effects-animation__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Direction</KuikaSmallText>
        <KuikaSelectbox
          size="sm"
          options={ANIMATION_DIRECTION_OPTIONS}
          value={item.direction ?? "normal"}
          onChange={(next: string | number | (string | number)[] | null): void => {
            if (typeof next === "string") onPatch({ direction: next as AnimationDirection });
          }}
          dataTestId="effects-animation-direction"
        />
      </div>
      <KeyframeStopEditor stops={item.keyframes ?? []} onChange={onKeyframesChange} />
    </div>
  );
};

export interface IEffectsAnimationsComponentProps {
  form: FormInstance<IKuikaStyle>;
}

interface IEffectsAnimationsComponentState {
  placeholder: never[];
}

class EffectsAnimationsComponent extends PureSdComponent<
  IEffectsAnimationsComponentProps & IWithSDProps,
  IEffectsAnimationsComponentState
> {
  constructor(props: IEffectsAnimationsComponentProps & IWithSDProps) {
    super(props);
    this.state = { placeholder: [] };
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  private isEffectsHidden = (): boolean =>
    this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden;

  private ensureEffectsSection = (fieldsValue: IKuikaStyle): void => {
    const kuikaid: string | undefined = this.props.sd.selectedKuikaIds[0];
    if (!fieldsValue.effects) {
      fieldsValue.effects = { effectsId: null, kuikaid: kuikaid ?? "" };
    }
  };

  // Mirror of EffectsTransitionsComponent.submitForm: setFieldsValue + submit,
  // never resetFields (that would unmount the PopoverInput subtree and close the
  // popover mid-edit).
  private submitForm = (fieldsValue: IKuikaStyle): void => {
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  private commitAnimations = (next: ILoopingAnimationItem[]): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.animations = next;
    }
    this.submitForm(fieldsValue);
  };

  private getAnimations = (): ILoopingAnimationItem[] =>
    (this.props.form.getFieldValue(["effects", "animations"]) as ILoopingAnimationItem[]) || [];

  private patchAnimationAt = (index: number, patch: Partial<ILoopingAnimationItem>): void => {
    const current: ILoopingAnimationItem[] = this.getAnimations();
    const target: ILoopingAnimationItem | undefined = current[index];
    if (!target) return;
    const next: ILoopingAnimationItem[] = current.map((it: ILoopingAnimationItem, i: number): ILoopingAnimationItem =>
      i === index ? { ...target, ...patch } : it
    );
    this.commitAnimations(next);
  };

  private selectPresetAt = (index: number, presetKey: LoopingPresetKey): void => {
    const current: ILoopingAnimationItem[] = this.getAnimations();
    const target: ILoopingAnimationItem | undefined = current[index];
    if (!target) return;
    // "Custom" keeps the current stops (so the user starts from what they see);
    // any real preset rebuilds the timeline + timing from the preset library.
    if (presetKey === "custom") {
      this.patchAnimationAt(index, { presetKey: "custom" });
      return;
    }
    const fresh: ILoopingAnimationItem = { ...defaultLoopingAnimationItem(presetKey), id: target.id };
    const next: ILoopingAnimationItem[] = current.map((it: ILoopingAnimationItem, i: number): ILoopingAnimationItem =>
      i === index ? fresh : it
    );
    this.commitAnimations(next);
  };

  // Editing the stops turns a preset into a custom timeline (presetKey "custom").
  private setKeyframesAt = (index: number, stops: ILoopingKeyframeStop[]): void => {
    this.patchAnimationAt(index, { keyframes: stops, presetKey: "custom" });
  };

  private removeAnimationAt = (index: number): void => {
    const current: ILoopingAnimationItem[] = this.getAnimations();
    this.commitAnimations(current.filter((_: ILoopingAnimationItem, i: number): boolean => i !== index));
  };

  private addAnimation = (): void => {
    this.commitAnimations([...this.getAnimations(), createAnimation()]);
  };

  render(): React.ReactNode {
    if (this.isEffectsHidden()) {
      return null;
    }

    return (
      <div className="effects-section__animations">
        <div className="effects-section__multi-header">
          <KuikaSmallText style={{ color: "var(--dark-7)", fontWeight: 500 }}>Looping Animations</KuikaSmallText>
          <div className="effects-section__multi-header-actions">
            <button
              type="button"
              aria-label="Add animation"
              className="effects-section__icon-button"
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                this.addAnimation();
              }}
              data-testid="effects-animation-add"
            >
              <KuikaSvg name="plus" width={14} height={14} />
            </button>
          </div>
        </div>
        <Form.Item noStyle shouldUpdate>
          {() => {
            const items: ILoopingAnimationItem[] = this.getAnimations();
            if (items.length === 0) {
              return null;
            }
            return (
              <div className="effects-section__multi-body">
                {items.map((item: ILoopingAnimationItem, index: number) => (
                  <PopoverInput
                    key={item.id}
                    value={summarizeLoopingAnimation(item)}
                    placeholder="Configure animation"
                    onClear={(): void => this.removeAnimationAt(index)}
                    popoverContent={
                      <AnimationPopoverContent
                        item={item}
                        onPatch={(patch: Partial<ILoopingAnimationItem>): void => this.patchAnimationAt(index, patch)}
                        onSelectPreset={(presetKey: LoopingPresetKey): void => this.selectPresetAt(index, presetKey)}
                        onKeyframesChange={(stops: ILoopingKeyframeStop[]): void => this.setKeyframesAt(index, stops)}
                      />
                    }
                    popoverTitle="Looping Animation"
                    popoverPosition="left"
                    popoverAlign="end"
                    dataTestId={`effects-animation-${index}`}
                  />
                ))}
              </div>
            );
          }}
        </Form.Item>
      </div>
    );
  }
}

export default withSD<IEffectsAnimationsComponentProps>(EffectsAnimationsComponent);
